import { Dispatch, MutableRefObject, SetStateAction, useCallback } from "react";
import {
  parseRelayerResponse,
  parseRelayerResponseSoft,
} from "./relayerResponse";
import { ethers, BigNumber } from "ethers";
import { getWeb3ProviderForChain } from "../../../utils/wallet/activeSigner";
import { getCurrentChainConfig } from "../../../config/chains";
import { getGaslessPerpRouter, getPerpDomainName } from "../../../utils/gaslessDomains";
import { getAgentWallet } from "../../../utils/oneClick";
import { InviteStatusResponse } from "../invite/useInviteStatus";
import {
  PERP_ORDER_HANDLER_ABI,
  toPerpTransactionError,
} from "./usePerpOrder.helpers";

interface UsePerpCancelArgs {
  account: string | null | undefined;
  masterAccount: string | null | undefined;
  inviteStatus: InviteStatusResponse | undefined;
  walletTransactionInFlightRef: MutableRefObject<boolean>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  revalidatePerpDataSoon: () => void;
}

export const useCancelPerpOrder = ({
  account,
  masterAccount,
  inviteStatus,
  walletTransactionInFlightRef,
  setIsLoading,
  revalidatePerpDataSoon,
}: UsePerpCancelArgs) =>
  useCallback(
    async (orderId: string): Promise<{ order_id: string; tx_hash: string }> => {
      if (!account) throw new Error("Not connected");
      if (!inviteStatus) {
        throw new Error("Checking invite access. Please try again in a moment.");
      }
      if (inviteStatus?.invite_required && !inviteStatus.allowed) {
        throw new Error("Invite code required to trade");
      }
      if (walletTransactionInFlightRef.current) {
        throw new Error(
          "Transaction already in progress. Please confirm or reject the open wallet request."
        );
      }

      const config = getCurrentChainConfig();
      const orderHandler = config.contracts.perpetualDex?.orderHandler;
      if (!orderHandler) throw new Error("Perp order handler not configured");

      walletTransactionInFlightRef.current = true;
      setIsLoading(true);
      try {
        const provider = await getWeb3ProviderForChain(config);
        const signer = provider.getSigner();

        // ── Gasless cancel: sign EIP-712 CancelOrder + relay it (user pays NO gas) ──
        // One-click signs with the stored agent key (no popup); otherwise the connected wallet
        // signs (one popup). The engine derives the order owner from order.trader and authorizes
        // the signer via _isAuthorizedFor. Mirrors the gasless order-placement path.
        const gaslessPerpRouter = getGaslessPerpRouter(config);
        const perpDomainName = getPerpDomainName(config);
        if (config.gasless && config.relayerUrl && gaslessPerpRouter) {
          const relayer = config.relayerUrl.replace(/\/+$/, "");
          const nonceRes = await fetch(`${relayer}/nonce/${account}?router=perpcancel`);
          const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
          const deadline = String(Math.floor(Date.now() / 1000) + 3600);
          const domain = {
            name: perpDomainName,
            version: "1",
            chainId: config.chainId,
            verifyingContract: gaslessPerpRouter,
          };
          const types = {
            CancelOrder: [
              { name: "orderId", type: "uint48" },
              { name: "nonce", type: "uint256" },
              { name: "deadline", type: "uint256" },
            ],
          };
          const message = {
            orderId: ethers.BigNumber.from(orderId),
            nonce,
            deadline,
          };
          // Sign with the MASTER's agent — when `account` is a subaccount, the order's trader is the
          // subaccount but the on-chain PerpEngine authorizes the master (or master's agent).
          const agentWallet = getAgentWallet(masterAccount || account, config.chainId);
          const signature = agentWallet
            ? await agentWallet._signTypedData(domain, types, message)
            : await signer._signTypedData(domain, types, message);
          const relayRes = await fetch(`${relayer}/relay`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              action: "cancelPerpOrder",
              params: { trader: account, orderId: String(orderId), nonce, deadline },
              signature,
            }),
          });
          const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
          if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
            throw new Error(relayJson?.error || "Gasless cancel failed. Please try again.");
          }
          revalidatePerpDataSoon();
          return { order_id: orderId, tx_hash: relayJson.txHash };
        }

        const orderHandlerContract = new ethers.Contract(
          orderHandler,
          PERP_ORDER_HANDLER_ABI,
          signer
        );
        const callArgs = [ethers.BigNumber.from(orderId), account] as const;
        const estimatedGas = await orderHandlerContract.estimateGas.cancelOrder(
          ...callArgs
        );
        const gasLimit = estimatedGas.mul(120).div(100);
        const tx = await orderHandlerContract.cancelOrder(...callArgs, {
          gasLimit,
        });
        await tx.wait(1);
        revalidatePerpDataSoon();
        return { order_id: orderId, tx_hash: tx.hash };
      } catch (error: any) {
        throw toPerpTransactionError(error, "Failed to cancel perp order");
      } finally {
        walletTransactionInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [account, inviteStatus, revalidatePerpDataSoon]
  );

export const useCancelPerpOrdersBatch = ({
  account,
  masterAccount,
  inviteStatus,
  walletTransactionInFlightRef,
  setIsLoading,
  revalidatePerpDataSoon,
}: UsePerpCancelArgs) =>
  useCallback(
    async (
      orderIds: string[]
    ): Promise<{ cancelled: string[]; tx_hash?: string }> => {
      if (!account) throw new Error("Not connected");
      if (!inviteStatus) {
        throw new Error("Checking invite access. Please try again in a moment.");
      }
      if (inviteStatus?.invite_required && !inviteStatus.allowed) {
        throw new Error("Invite code required to trade");
      }
      if (walletTransactionInFlightRef.current) {
        throw new Error(
          "Transaction already in progress. Please confirm or reject the open wallet request."
        );
      }

      const ids = Array.from(new Set(orderIds))
        .map((orderId) =>
          orderId && /^\d+$/.test(orderId)
            ? BigNumber.from(orderId)
            : undefined
        )
        .filter((orderId): orderId is BigNumber => !!orderId);

      if (!ids.length) {
        return { cancelled: [] };
      }

      const config = getCurrentChainConfig();
      const orderHandler = config.contracts.perpetualDex?.orderHandler;
      if (!orderHandler) throw new Error("Perp order handler not configured");

      walletTransactionInFlightRef.current = true;
      setIsLoading(true);
      try {
        const provider = await getWeb3ProviderForChain(config);
        const signer = provider.getSigner();
        // ── Gasless "cancel all": one signed CancelBatchOrders → relay (user pays NO gas) ──
        // One-click signs with the agent key (no popup); otherwise the connected wallet signs once.
        const gaslessPerpRouter = getGaslessPerpRouter(config);
        const perpDomainName = getPerpDomainName(config);
        if (config.gasless && config.relayerUrl && gaslessPerpRouter) {
          const relayer = config.relayerUrl.replace(/\/+$/, "");
          const nonceRes = await fetch(`${relayer}/nonce/${account}?router=perpcancel`);
          const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
          const deadline = String(Math.floor(Date.now() / 1000) + 3600);
          const idNumbers = ids.map((id) => id.toNumber());
          const orderIdsHash = ethers.utils.solidityKeccak256(["uint48[]"], [idNumbers]);
          const domain = {
            name: perpDomainName,
            version: "1",
            chainId: config.chainId,
            verifyingContract: gaslessPerpRouter,
          };
          const types = {
            CancelBatchOrders: [
              { name: "account", type: "address" },
              { name: "orderIdsHash", type: "bytes32" },
              { name: "nonce", type: "uint256" },
              { name: "deadline", type: "uint256" },
            ],
          };
          const message = { account, orderIdsHash, nonce, deadline };
          // Sign with the MASTER's agent — when `account` is a subaccount, the order's trader is the
          // subaccount but the on-chain PerpEngine authorizes the master (or master's agent).
          const agentWallet = getAgentWallet(masterAccount || account, config.chainId);
          const signature = agentWallet
            ? await agentWallet._signTypedData(domain, types, message)
            : await signer._signTypedData(domain, types, message);
          const relayRes = await fetch(`${relayer}/relay`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              action: "cancelPerpOrderBatch",
              params: { trader: account, orderIds: idNumbers, nonce, deadline },
              signature,
            }),
          });
          const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
          if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
            throw new Error(relayJson?.error || "Gasless cancel failed. Please try again.");
          }
          revalidatePerpDataSoon();
          return { cancelled: ids.map((id) => id.toString()), tx_hash: relayJson.txHash };
        }

        const orderHandlerContract = new ethers.Contract(
          orderHandler,
          PERP_ORDER_HANDLER_ABI,
          signer
        );
        const estimatedGas =
          await orderHandlerContract.estimateGas.cancelOrderBatch(ids, account);
        const tx = await orderHandlerContract.cancelOrderBatch(ids, account, {
          gasLimit: estimatedGas.mul(120).div(100),
        });
        await tx.wait(1);
        revalidatePerpDataSoon();
        return {
          cancelled: ids.map((orderId) => orderId.toString()),
          tx_hash: tx.hash,
        };
      } catch (error: any) {
        throw toPerpTransactionError(error, "Failed to cancel perp orders");
      } finally {
        walletTransactionInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [account, inviteStatus, revalidatePerpDataSoon]
  );
